// candidate-commit-without-controller-paths.ts
//
// N-04 (dogfood worker thật 2026-09-15, mission ME-2026-11ce3c): the mission
// worktree tracks its own infrastructure — `.claude/pack/**` (the pinned pack),
// `.claude/settings.json`, `avatar-tools.json`, the symlink farm — because
// `ensureBootstrapCommit` stages `.` and MISSION_IGNORE_ENTRIES only hides
// `.claude/state/`. Measured: the bootstrap commit alone was 815 files /
// 155,513 lines, 812 of them under `.claude/`. That tracking is worth keeping
// INSIDE the worktree: `recoverOwnedCheckout`'s `git reset --hard` restores a
// tampered hook every tick and the verifier's inventory diff can see an edit
// to one. But the branch `pushDraftMergeRequest` sends is the one reviewers
// read and the one that merges — it must not carry the controller's files.
//
// So the pushed ref is a SEPARATE commit built here from the mission HEAD's
// tree with the controller-owned top-level paths reset to the base's, on the
// base SHA (one squashed candidate: a clean diff; no pack blob is reachable). Built through a TEMPORARY index (`GIT_INDEX_FILE`), so the mission
// worktree's own index/HEAD are never touched — the release flow keeps using
// the mission HEAD (`candidateSha`) as the key for review-staging, G3 and
// deploy receipts; only the ref on the remote differs, and the commit's
// trailers say which mission HEAD it was derived from.
import { promises as fs } from "node:fs";
import { tmpdir } from "node:os";
import { join } from "node:path";
import { buildCandidateCommitMessage } from "./candidate-commit-message-builder.js";
import {
  gitOrThrow,
  restoreBaseEntriesOfStrippedPaths,
} from "./candidate-commit-restore-base-entries.js";
import type { GitRunner } from "./commit-owner-git-ops.js";

// Message construction split into candidate-commit-message-builder.ts to
// keep this file ≤200 lines — re-exported so callers only import ONE module.
export { buildCandidateCommitMessage } from "./candidate-commit-message-builder.js";

/** Top-level worktree paths that belong to the controller, never to the product. */
export const CONTROLLER_OWNED_TOP_LEVEL_PATHS: readonly string[] = [".claude"];

export interface BuildCandidateCommitInput {
  gitRunner: GitRunner;
  repoDir: string;
  /** Mission HEAD — the tree to derive from and the SHA the trailers record. */
  candidateSha: string;
  /** Mission base (target branch at init) — the parent of the pushed commit. */
  baseSha: string;
  missionId: string;
  /** Override for tests; production uses CONTROLLER_OWNED_TOP_LEVEL_PATHS. */
  stripPaths?: readonly string[];
  /** A2: `<ticket>: <area>` lines the done tickets' workers declared unverified (draft-merge-request-push.ts collects them). */
  uncoveredAreas?: readonly string[];
  /** A2: `<ticket>: <work>` lines left for a person. */
  remainingWork?: readonly string[];
  /** A2: done tickets whose worker declared an EMPTY uncovered list (a claim). */
  uncoveredDeclaredNone?: readonly string[];
  /** A2: done tickets whose worker never sent the field (silence). */
  uncoveredNotDeclared?: readonly string[];
  /** D16: see candidate-commit-message-builder.ts. */
  deployRecipeId?: string | null;
  /** V2-1 E11: ledger lines (decision-ledger-merge-request-block.ts); empty/absent ⇒ no block. */
  decisionLines?: readonly string[];
  /** V2-1 B6: applied to the WHOLE message before the commit is built (draft-merge-request-push.ts). */
  redactDescription?: (message: string) => string;
}

export interface BuildCandidateCommitResult {
  /** The commit to push — differs from `candidateSha` by construction. */
  pushedSha: string;
  tree: string;
}

/**
 * Code review 260915 M1: `commit-tree -p baseSha` mints a commit for ANY
 * `baseSha`, ancestor or not. If the target branch moved after the mission
 * read its base at init (a force-push, or simply an unrelated push), the
 * pushed candidate would sit on a parent the mission never built on and
 * "Ticket commits:" (`git log base..HEAD`) would list commits that are not
 * the mission's — a wrong MR that no log on this machine would flag. Git's
 * own ancestry check is the arbiter: exit 0 = ancestor (a commit counts as
 * its own ancestor, so base == candidate stays valid), 1 = not, anything
 * else = a real git failure surfaced like every other plumbing error here.
 */
async function assertBaseIsAncestorOfCandidate(input: BuildCandidateCommitInput): Promise<void> {
  const res = await input.gitRunner(
    ["merge-base", "--is-ancestor", input.baseSha, input.candidateSha],
    input.repoDir,
  );
  if (res.status === 0) return;
  if (res.status === 1) {
    throw new Error(
      `candidate-commit: baseSha ${input.baseSha} không phải tổ tiên của candidateSha ` +
        `${input.candidateSha} — target branch có thể đã bị force-push sau khi mission init; ` +
        "không dựng commit candidate.",
    );
  }
  throw new Error(
    `candidate-commit: git merge-base --is-ancestor thất bại (status ${res.status}): ${res.stderr}`,
  );
}

/**
 * Builds `pushedSha`: tree(candidateSha) with each controller-owned path
 * reset to the base's entries (none ⇒ removed), parent = baseSha. Plumbing
 * (`read-tree`/`rm --cached`/`write-tree`/`commit-tree`) on a temporary index
 * — the worktree, its index and HEAD are left exactly as they were, even if
 * this throws midway. Refuses when `baseSha` is not an ancestor (M1 above).
 */
export async function buildCandidateCommitWithoutControllerPaths(
  input: BuildCandidateCommitInput,
): Promise<BuildCandidateCommitResult> {
  await assertBaseIsAncestorOfCandidate(input);
  const stripped = input.stripPaths ?? CONTROLLER_OWNED_TOP_LEVEL_PATHS;
  const scratch = await fs.mkdtemp(join(tmpdir(), "avatar-candidate-index-"));
  const env = { GIT_INDEX_FILE: join(scratch, "index") };
  try {
    await gitOrThrow(input.gitRunner, ["read-tree", input.candidateSha], input.repoDir, env);
    await gitOrThrow(
      input.gitRunner,
      ["rm", "-r", "-q", "--cached", "--ignore-unmatch", "--", ...stripped],
      input.repoDir,
      env,
    );
    await restoreBaseEntriesOfStrippedPaths(input, stripped, env);
    const tree = await gitOrThrow(input.gitRunner, ["write-tree"], input.repoDir, env);
    // Per-ticket history for the reviewer (D-N04): read from the real repo,
    // not the temporary index — `git log` needs no index at all.
    const ticketCommits = (
      await gitOrThrow(
        input.gitRunner,
        ["log", "--format=%h %s", `${input.baseSha}..${input.candidateSha}`],
        input.repoDir,
      )
    )
      .split("\n")
      .filter(Boolean);
    const redact = input.redactDescription ?? ((message: string) => message);
    const message = redact(
      buildCandidateCommitMessage({
        missionId: input.missionId,
        candidateSha: input.candidateSha,
        baseSha: input.baseSha,
        stripped,
        ticketCommits,
        uncoveredAreas: input.uncoveredAreas,
        remainingWork: input.remainingWork,
        uncoveredDeclaredNone: input.uncoveredDeclaredNone,
        uncoveredNotDeclared: input.uncoveredNotDeclared,
        deployRecipeId: input.deployRecipeId,
        decisionLines: input.decisionLines,
      }),
    );
    // X2 review H1 (2026-09-29): pin both dates to the candidate's own commit
    // time, so the same candidate always yields the same pushed SHA. A re-run
    // (e.g. waiting for the MR pipeline, `pipeline_sha: not_reported`) then
    // re-pushes the identical commit instead of a non-fast-forward sibling.
    const candidateTime = await gitOrThrow(
      input.gitRunner,
      ["show", "-s", "--format=%ct", input.candidateSha],
      input.repoDir,
    );
    const pinnedDate = `@${candidateTime} +0000`;
    const pushedSha = await gitOrThrow(
      input.gitRunner,
      ["commit-tree", tree, "-p", input.baseSha, "-m", message],
      input.repoDir,
      { GIT_AUTHOR_DATE: pinnedDate, GIT_COMMITTER_DATE: pinnedDate },
    );
    return { pushedSha, tree };
  } finally {
    await fs.rm(scratch, { recursive: true, force: true });
  }
}
